import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  FormFeedback,
  Form,
} from "reactstrap";
import Select from "react-select";
import {
  associateBranchUnits,
  resourcesUnits,
} from "../../../helpers/backend_helper";
import { getStorageCompany } from "../../../helpers/api_helper";
import { DestroyAlertHelper } from "../../../helpers/alerts/alertHelper";
import { useFormik } from "formik";
import * as Yup from "yup";
import DataTableHelper from "../../../helpers/tables/DataTableHelper";

const unitAction = {
  ADD: "Add",
  REMOVE: "Remove",
};

const UnitsModal = ({ modal, setModal }) => {
  const company = getStorageCompany();

  const [isLoadingUnits, setIsLoadingUnits] = useState(true);
  const [isLoadingRamalUnits, setIsLoadingRamalUnits] = useState(true);
  const [isUpdatingRamalUnits, setIsUpdatingRamalUnits] = useState(false);

  const [units, setUnits] = useState([]);
  const [ramalUnits, setRamalUnits] = useState([]);

  const [filters] = useState({
    orderBy: "id",
    order: "asc",
    companySession: company.id,
    select: true,
    route_id: modal.route_id,
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      active: null,
    },
    validationSchema: Yup.object({
      active: Yup.boolean().required("Requerido"),
    }),
  });

  const ramalUnitsIDS = ramalUnits.map((unit) => unit.id);
  const unitsFiltered = units.filter(
    (unit) => !ramalUnitsIDS.includes(unit.id)
  );

  const handleClose = () => {
    setModal({ ...modal, show: false });
  };

  const fetchRamalUnits = useCallback(async () => {
    try {
      let config = {
        params: {
          ...filters,
          route_id: "",
          ramal_id: modal.id,
        },
      };

      const response = await resourcesUnits("get", config);

      setRamalUnits(response.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingRamalUnits(false);
    }
  }, []);

  const handleAssociateBranchUnit = async (action, unitId) => {
    setIsUpdatingRamalUnits(true);
    try {
      const data = {
        action,
        unitId,
      };

      await associateBranchUnits(modal.id, data);
      await fetchRamalUnits();
    } catch (error) {
      console.log(error);
    } finally {
      setIsUpdatingRamalUnits(false);
    }
  };

  useEffect(() => {
    fetchRamalUnits();
  }, [fetchRamalUnits]);

  useEffect(() => {
    const getUnits = async () => {
      try {
        let config = { params: filters };
        const response = await resourcesUnits("get", config);

        const parsedUnits = response.data.map((unit) => ({
          label: unit.number,
          value: unit.id,
          id: unit.id,
        }));

        setUnits(parsedUnits);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingUnits(false);
      }
    };
    getUnits();
  }, [filters]);

  const destroy = (action, unitId) => {
    DestroyAlertHelper(async (confirmed) => {
      if (!confirmed) {
        return;
      }

      handleAssociateBranchUnit(action, unitId);
    });
  };

  const columns = [
    {
      id: "unidad",
      name: "Unidad",
      selector: (row) => row.number,
      sortable: true,
    },
    {
      id: "tipo",
      name: "Tipo",
      selector: (row) => row.type,
      sortable: true,
    },
    {
      id: "placas",
      name: "Placas",
      selector: (row) => row.plates,
      sortable: true,
    },
    {
      id: "actions",
      name: "Acciones",
      selector: (row) => row.name,
      button: true,
      cell: (row) => (
        <div className="d-flex justify-content-between">
          <div className="pe-2">
            <button
              className="btn btn-danger btn-sm"
              title="Eliminar unidad"
              onClick={() => destroy(unitAction.REMOVE, row.id)}
            >
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div>
      <Modal isOpen={modal.show}>
        <ModalHeader className="bg-primary">{"Ramales - Unidades"}</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <ModalBody>
            <div className="row">
              <div className="col-sm-12">
                <label className="form-label">
                  {isLoadingUnits && <Spinner size="sm"></Spinner>} Agregar
                  unidad:{" "}
                </label>
                <Select
                  placeholder="Selecciona la unidad"
                  isDisabled={isLoadingUnits}
                  className="basic-single"
                  classNamePrefix="select"
                  isClearable={true}
                  value={
                    unitsFiltered.filter(
                      (e) => e.value == validation.values.active
                    )[0] ?? null
                  }
                  isSearchable={true}
                  name="active"
                  options={unitsFiltered}
                  onChange={(option, meta) => {
                    handleAssociateBranchUnit(unitAction.ADD, option.value);

                    //validation.setFieldValue("active", option.value, true);
                  }}
                  invalid={
                    validation.touched.active && validation.errors.active
                  }
                />
                <FormFeedback type="invalid">
                  {validation.errors.active}
                </FormFeedback>
              </div>
            </div>
            <div className="row">
              <div className="col-sm-24">
                {isUpdatingRamalUnits && (
                  <div className="pt-4 text-center">
                    <Spinner color="dark" size="sm" /> Actualizando unidades
                  </div>
                )}
              </div>
              <div className="col-sm-24 pt-4">
                <DataTableHelper
                  tittle="Unidades"
                  columns={columns}
                  items={ramalUnits}
                  pending={isLoadingRamalUnits}
                  filterInp={false}
                  pagination={false}
                  config={{}}
                  filters={{}}
                ></DataTableHelper>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleClose}>
              <i className="fa fa-times"></i> Cancelar
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default UnitsModal;
